/**
 * Typed API client (ARCHITECTURE.md §8).
 *
 * `hc<AppType>` gives a fully type-safe RPC surface derived from the Worker's
 * chained Hono routes — `AppType` is imported **type-only** so no Worker code is
 * pulled into the browser bundle. The base is same-origin `/api` (Vite proxies
 * it to `wrangler dev` locally; in production the Worker owns `/api/*`).
 *
 * `credentials: 'include'` is required so the `HttpOnly` session cookie rides
 * along on every request — the SPA never sees or handles a token (§8, §10).
 */
import { hc } from "hono/client";
import type { AppType } from "@richrich/api";

/** Same-origin API base. Keep in one place so tests/preview can override later. */
export const API_BASE = "/api";

/** The typed client. All calls send the session cookie. */
export const api = hc<AppType>(API_BASE, {
  init: { credentials: "include" },
});

/**
 * A structured API failure. Carries the HTTP status so callers (and the global
 * query-error handler) can branch on `401` (session lost) vs. everything else.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** `true` when a session-expiry redirect to `/login` is warranted. */
export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/** `true` when the instance has not been claimed yet (`409 UNCLAIMED`). */
export function isUnclaimed(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === "UNCLAIMED";
}

interface ErrorEnvelope {
  error?: unknown;
  message?: unknown;
}

/**
 * The structural subset of a fetch `Response` that {@link unwrap} needs. Both the
 * DOM `Response` and hono's typed `ClientResponse` (whose `json()`/`status` are
 * narrowed and so are *not* nominally a `Response`) satisfy this, so `unwrap` can
 * consume `hc<AppType>` results directly without a cast.
 */
export interface ResponseLike {
  readonly ok: boolean;
  readonly status: number;
  readonly statusText: string;
  json(): Promise<unknown>;
}

/**
 * Unwrap a response from an `hc` call into typed JSON, throwing {@link ApiError}
 * on any non-2xx. Route loaders and mutations funnel through this so failures are
 * uniform and the global handler can react to `401`/`409`.
 */
export async function unwrap<T>(res: ResponseLike): Promise<T> {
  if (res.ok) {
    return (await res.json()) as T;
  }
  let code = `HTTP_${res.status}`;
  let message = res.statusText || "Request failed";
  try {
    const body = (await res.json()) as ErrorEnvelope;
    if (typeof body.error === "string") code = body.error;
    if (typeof body.message === "string") message = body.message;
  } catch {
    // Non-JSON error body (e.g. an asset 404) — keep the status-derived defaults.
  }
  throw new ApiError(res.status, code, message);
}

/**
 * Like {@link unwrap}, but a `404` resolves to `null` instead of throwing — the
 * "no account / no snapshot yet" shape several portfolio reads answer with
 * (§7). Any other status still throws {@link ApiError}.
 */
export async function unwrapOrNull<T>(res: ResponseLike): Promise<T | null> {
  try {
    return await unwrap<T>(res);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}
